import React, { useEffect, useState } from "react";
import { Card, Typography, Tag, Space, DatePicker, Input, Button, Select, Tooltip, message, Modal, Checkbox, Row, Col } from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { TransactionOutlined, SearchOutlined, ReloadOutlined, DollarOutlined, WalletOutlined, TeamOutlined, DownloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import * as adminactions from "store/actions/AuthActions";
import axios from "axios";
import useUrlFilters from "hooks/useUrlFilters";

// Available columns for export
const EXPORT_COLUMNS = [
    { key: 'serial_number', label: 'S.No', default: true },
    { key: 'bank_account', label: 'Bank Account', default: true },
    { key: 'transaction_id', label: 'Transaction ID', default: true },
    { key: 'transaction_date', label: 'Transaction Date', default: true },
    { key: 'value_date', label: 'Value Date', default: true },
    { key: 'transaction_type', label: 'Type', default: true },
    { key: 'amount', label: 'Amount', default: true },
    { key: 'balance', label: 'Balance', default: true },
    { key: 'remarks', label: 'Remarks', default: true },
    { key: 'cheque_ref_number', label: 'Cheque/Ref No', default: false },
    { key: 'category', label: 'Category', default: false },
    { key: 'is_reconciled', label: 'Reconciled', default: false },
];

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const organization = process.env.REACT_APP_ORGANIZATION_UUID;

const URL_FILTERS = {
    search: { type: 'string' },
    transactionType: { type: 'string', key: 'transaction_type' },
    dateRange: { type: 'dateRange', keys: ['start_date', 'end_date'] },
    bankAccount: { type: 'string', key: 'bank_account' },
    page: { type: 'number', default: 1 },
    pageSize: { type: 'number', key: 'page_size', default: 50 },
};
const NO_RANGE = [null, null];

const TransactionsView = () => {
    const navigate = useNavigate();
    const [filters, setFilters, clearFilters] = useUrlFilters(URL_FILTERS);
    const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);

    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({ current: filters.page, pageSize, total: 0 });

    // Filters, search and page live in the URL so the view can be shared.
    const searchText = filters.search || "";
    const [searchTextLive, setSearchTextLive] = useState(searchText);
    useEffect(() => setSearchTextLive(searchText), [searchText]);
    const transactionType = filters.transactionType || "";
    const dateRange = filters.dateRange || NO_RANGE;
    const [bankAccounts, setBankAccounts] = useState([]);
    const selectedBankAccount = filters.bankAccount || null;
    const [exporting, setExporting] = useState(false);
    const [exportModalVisible, setExportModalVisible] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState(
        EXPORT_COLUMNS.filter(col => col.default).map(col => col.key)
    );

    const handleExportClick = () => {
        setExportModalVisible(true);
    };

    const handleColumnChange = (checkedValues) => {
        setSelectedColumns(checkedValues);
    };

    const handleSelectAllColumns = (e) => {
        if (e.target.checked) {
            setSelectedColumns(EXPORT_COLUMNS.map(col => col.key));
        } else {
            setSelectedColumns([]);
        }
    };

    const handleExport = async () => {
        if (selectedColumns.length === 0) {
            message.warning('Please select at least one column to export');
            return;
        }

        setExporting(true);
        setExportModalVisible(false);
        try {
            const params = new URLSearchParams();
            params.append('organization', organization);

            if (searchText) params.append('search', searchText);
            if (transactionType) params.append('transaction_type', transactionType);
            if (dateRange[0]) params.append('transaction_date__gte', dateRange[0].format('YYYY-MM-DD'));
            if (dateRange[1]) params.append('transaction_date__lte', dateRange[1].format('YYYY-MM-DD'));
            if (selectedBankAccount) params.append('bank_account', selectedBankAccount);

            // Add selected columns
            params.append('columns', selectedColumns.join(','));

            const response = await axios.get(`/accounting/bank-transaction/export/?${params.toString()}`, {
                responseType: 'blob',
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'bank_transactions.xlsx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success('Transactions exported successfully');
        } catch (error) {
            console.error('Export error:', error);
            message.error('Failed to export transactions');
        } finally {
            setExporting(false);
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const data = await adminactions.loadBankAccounts(organization);
            if (data) {
                setBankAccounts(data.results || data || []);
            }
        } catch (error) {
            console.error("Error fetching bank accounts:", error);
        }
    };

    const fetchTransactions = async (page = 1, pageSize = 50) => {
        setLoading(true);
        try {
            const params = {
                organization: organization,
                page: page,
                page_size: pageSize,
            };

            if (searchText) params.search = searchText;
            if (transactionType) params.transaction_type = transactionType;
            if (dateRange[0]) params.transaction_date__gte = dateRange[0].format('YYYY-MM-DD');
            if (dateRange[1]) params.transaction_date__lte = dateRange[1].format('YYYY-MM-DD');
            if (selectedBankAccount) params.bank_account = selectedBankAccount;

            const data = await adminactions.loadBankTransactions(params);
            if (data) {
                setTransactions(data.results || data);
                setPagination({
                    current: page,
                    pageSize: pageSize,
                    total: data.count || data.length,
                });
            }
        } catch (error) {
            console.error("Error fetching transactions:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBankAccounts();
    }, []);

    // Fetch transactions when filters or page change
    useEffect(() => {
        fetchTransactions(filters.page, pageSize);
    }, [searchText, transactionType, dateRange, selectedBankAccount, filters.page, pageSize]);

    const getTransactionTypeTag = (type) => {
        return type === 'DEPOSIT'
            ? <Tag color="green">DEPOSIT</Tag>
            : <Tag color="red">WITHDRAWAL</Tag>;
    };

    const columns = [
        {
            title: "Bank Account",
            dataIndex: "bank_account_display",
            key: "bank_account_display",
            width: 180,
            ellipsis: true,
            render: (value, record) => {
                const bankName = record.bank_account_name || record.bank_name || '';
                const accountNum = record.bank_account_number || record.account_number || '';
                if (bankName || accountNum) {
                    return <span>{bankName} {accountNum ? `- ${accountNum.slice(-4)}` : ''}</span>;
                }
                return '-';
            },
        },
        {
            title: "Transaction ID",
            dataIndex: "transaction_id",
            key: "transaction_id",
            width: 150,
            ellipsis: true,
        },
        {
            title: "Date",
            dataIndex: "transaction_date",
            key: "transaction_date",
            width: 100,
            render: (date) => <span style={{ whiteSpace: 'nowrap' }}>{date ? dayjs(date).format('DD/MM/YYYY') : '-'}</span>,
        },
        {
            title: "Type",
            dataIndex: "transaction_type",
            key: "transaction_type",
            width: 100,
            render: (type) => getTransactionTypeTag(type),
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            width: 120,
            align: "right",
            render: (value, record) => (
                <span style={{ whiteSpace: 'nowrap', color: record.transaction_type === 'DEPOSIT' ? '#52c41a' : '#f5222d' }}>
                    {record.transaction_type === 'DEPOSIT' ? '+' : '-'}₹{parseFloat(value).toLocaleString('en-IN')}
                </span>
            ),
        },
        {
            title: "Balance",
            dataIndex: "balance",
            key: "balance",
            width: 120,
            align: "right",
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value ? `₹${parseFloat(value).toLocaleString('en-IN')}` : '-'}</span>,
        },
        {
            title: "Amt Reconciled",
            dataIndex: "reconciled_amount",
            key: "reconciled_amount",
            width: 130,
            align: "right",
            render: (value, record) => {
                if (!value) return <span style={{ color: '#999' }}>-</span>;
                const txnAmount = parseFloat(record.amount);
                const reconAmount = parseFloat(value);
                const isFullyReconciled = Math.abs(txnAmount - reconAmount) < 0.01;
                const isPartial = reconAmount > 0 && reconAmount < txnAmount;
                return (
                    <Tooltip title={isFullyReconciled ? "Fully Reconciled" : isPartial ? "Partially Reconciled" : ""}>
                        <span style={{
                            whiteSpace: 'nowrap',
                            color: isFullyReconciled ? '#52c41a' : isPartial ? '#faad14' : '#1890ff',
                            fontWeight: isFullyReconciled ? 600 : 400
                        }}>
                            ₹{reconAmount.toLocaleString('en-IN')}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            width: 200,
            ellipsis: true,
        },
        {
            title: "Cheque/Ref",
            dataIndex: "cheque_ref_number",
            key: "cheque_ref_number",
            width: 120,
            ellipsis: true,
        },
        {
            title: "Reconciled",
            dataIndex: "is_reconciled",
            key: "is_reconciled",
            width: 90,
            render: (value) => value ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
        },
        {
            title: "Linked Records",
            dataIndex: "linked_records",
            key: "linked_records",
            width: 180,
            render: (_, record) => {
                const links = [];

                // Check for linked expenses
                if (record.reconciled_with_expense_id) {
                    const expenseIds = Array.isArray(record.reconciled_with_expense_id)
                        ? record.reconciled_with_expense_id
                        : [record.reconciled_with_expense_id];
                    expenseIds.forEach((id, idx) => {
                        links.push(
                            <Tooltip title="View Expense" key={`expense-${id}`}>
                                <Tag
                                    color="orange"
                                    style={{ cursor: 'pointer', marginBottom: 2 }}
                                    onClick={() => navigate(`/expenses/edit/${id}`)}
                                >
                                    <DollarOutlined /> Expense {expenseIds.length > 1 ? idx + 1 : ''}
                                </Tag>
                            </Tooltip>
                        );
                    });
                }

                // Check for linked payments (with currency info)
                if (record.reconciled_with_payment_id) {
                    const paymentIds = Array.isArray(record.reconciled_with_payment_id)
                        ? record.reconciled_with_payment_id
                        : [record.reconciled_with_payment_id];
                    const paymentData = record.reconciled_with_payment_data || [];

                    paymentIds.forEach((id, idx) => {
                        // Find payment data for this payment
                        const pData = paymentData.find(p => p.id === id);
                        const isForeignCurrency = pData && pData.currency_code && pData.currency_code !== 'INR';

                        // Build tooltip content
                        let tooltipContent = "View Payment";
                        if (isForeignCurrency && pData) {
                            tooltipContent = (
                                <div>
                                    <div>View Payment</div>
                                    <div style={{ fontSize: 11, marginTop: 4 }}>
                                        Original: {pData.currency_code} {parseFloat(pData.original_amount).toLocaleString()}
                                    </div>
                                    <div style={{ fontSize: 11 }}>
                                        Rate: 1 {pData.currency_code} = ₹{parseFloat(pData.exchange_rate).toFixed(2)}
                                    </div>
                                </div>
                            );
                        }

                        links.push(
                            <Tooltip title={tooltipContent} key={`payment-${id}`}>
                                <Tag
                                    color="green"
                                    style={{ cursor: 'pointer', marginBottom: 2 }}
                                    onClick={() => navigate(`/payments/edit/${id}`)}
                                >
                                    <WalletOutlined /> Payment {paymentIds.length > 1 ? idx + 1 : ''}
                                    {isForeignCurrency && <span style={{ marginLeft: 4, fontSize: 10 }}>({pData.currency_code})</span>}
                                </Tag>
                            </Tooltip>
                        );
                    });
                }

                // Check for linked payroll
                if (record.linked_payroll && record.linked_payroll.length > 0) {
                    record.linked_payroll.forEach((id, idx) => {
                        links.push(
                            <Tooltip title="View Payroll Record" key={`payroll-${id}`}>
                                <Tag
                                    color="blue"
                                    style={{ cursor: 'pointer', marginBottom: 2 }}
                                    onClick={() => navigate(`/payroll/history/${id}`)}
                                >
                                    <TeamOutlined /> Payroll {record.linked_payroll.length > 1 ? idx + 1 : ''}
                                </Tag>
                            </Tooltip>
                        );
                    });
                }

                return links.length > 0 ? (
                    <Space size={[0, 4]} wrap>
                        {links}
                    </Space>
                ) : (
                    <span style={{ color: '#999' }}>-</span>
                );
            },
        },
        {
            title: "Value Date",
            dataIndex: "value_date",
            key: "value_date",
            width: 100,
            render: (date) => <span style={{ whiteSpace: 'nowrap' }}>{date ? dayjs(date).format('DD/MM/YYYY') : '-'}</span>,
        },
    ];

    const handleTableChange = (newPagination) => {
        const sizeChanged = newPagination.pageSize !== pageSize;
        setFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
    };

    const handleSearch = () => {
        setFilters({ search: searchTextLive, page: 1 });
    };

    const handleReset = () => {
        setSearchTextLive("");
        clearFilters(['pageSize']);
    };

    const onTransactionTypeChange = (value) => {
        setFilters({ transactionType: value || null, page: 1 });
    };

    const onDateRangeChange = (dates) => {
        setFilters({ dateRange: dates && dates[0] && dates[1] ? dates : null, page: 1 });
    };

    const onBankAccountChange = (value) => {
        setFilters({ bankAccount: value || null, page: 1 });
    };

    return (
        <div style={{ padding: "24px" }}>
            <Card
                title={
                    <Space>
                        <TransactionOutlined />
                        <Title level={4} style={{ margin: 0 }}>Bank Statement Transactions (View Only)</Title>
                    </Space>
                }
            >
                <Space style={{ marginBottom: 16 }} wrap>
                    <Input
                        placeholder="Search transaction ID, remarks..."
                        prefix={<SearchOutlined />}
                        value={searchTextLive}
                        onChange={(e) => setSearchTextLive(e.target.value)}
                        onPressEnter={handleSearch}
                        style={{ width: 250 }}
                    />
                    <Select
                        placeholder="Select Bank Account"
                        value={selectedBankAccount}
                        onChange={onBankAccountChange}
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 220 }}
                        options={bankAccounts.map(ba => ({
                            value: String(ba.id),
                            label: `${ba.bank_name || ba.bank} - ${ba.account_number}`,
                        }))}
                    />
                    <Select
                        placeholder="Transaction Type"
                        value={transactionType || undefined}
                        onChange={onTransactionTypeChange}
                        allowClear
                        style={{ width: 150 }}
                    >
                        <Option value="DEPOSIT">Deposit</Option>
                        <Option value="WITHDRAWAL">Withdrawal</Option>
                    </Select>
                    <RangePicker
                        value={dateRange}
                        onChange={onDateRangeChange}
                        format="DD/MM/YYYY"
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        Search
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleReset}>
                        Reset
                    </Button>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={handleExportClick}
                        loading={exporting}
                        style={{ marginLeft: 8 }}
                    >
                        Export Excel
                    </Button>
                </Space>

                <Table
                    columns={columns}
                    dataSource={transactions}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`,
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1800 }}
                    size="small"
                />
            </Card>

            {/* Export Column Selection Modal */}
            <Modal
                title={
                    <Space>
                        <DownloadOutlined />
                        Export Transactions
                    </Space>
                }
                open={exportModalVisible}
                onCancel={() => setExportModalVisible(false)}
                onOk={handleExport}
                okText="Export"
                okButtonProps={{ icon: <DownloadOutlined />, loading: exporting }}
                width={500}
            >
                <div style={{ marginBottom: 16 }}>
                    <p style={{ marginBottom: 8, color: '#666' }}>
                        Select the columns you want to include in the export:
                    </p>
                    <Checkbox
                        onChange={handleSelectAllColumns}
                        checked={selectedColumns.length === EXPORT_COLUMNS.length}
                        indeterminate={selectedColumns.length > 0 && selectedColumns.length < EXPORT_COLUMNS.length}
                        style={{ marginBottom: 12 }}
                    >
                        <strong>Select All</strong>
                    </Checkbox>
                </div>

                <Checkbox.Group
                    value={selectedColumns}
                    onChange={handleColumnChange}
                    style={{ width: '100%' }}
                >
                    <Row gutter={[8, 8]}>
                        {EXPORT_COLUMNS.map(col => (
                            <Col span={12} key={col.key}>
                                <Checkbox value={col.key}>{col.label}</Checkbox>
                            </Col>
                        ))}
                    </Row>
                </Checkbox.Group>

                {dateRange[0] && dateRange[1] && (
                    <div style={{ marginTop: 16, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
                        <small style={{ color: '#666' }}>
                            Date Range: {dateRange[0].format('DD/MM/YYYY')} - {dateRange[1].format('DD/MM/YYYY')}
                        </small>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default TransactionsView;
